import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PastBlocksTable from "src/components/orderBlock/PastBlocksTable";
import RetestOrderTable from "src/components/orderBlock/RetestOrderTable";
import OrderBlockChart from "src/components/orderBlock/OrderBlockChart";
import {
  fetchWrapper,
  OrderBlockForPastDays,
  OrderBlockForRetest,
} from "src/helpers";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { alertService } from "src/services";

type PastSymbolData = {
  symbol: string;
  ob_date?: string;
};

type RetestSymbolData = {
  symbol: string;
  ob_date?: string;
  retest_date?: string;
};

const normalizeSymbol = (value: string) => value.replace(/\.NS$/, "");

const OrderBlock = () => {
  const user = getLocalStorageUser();
  const tokenRef = useRef(user.token);
  tokenRef.current = user.token;

  const [isPastLoading, setIsPastLoading] = useState(true);
  const [isRetestLoading, setIsRetestLoading] = useState(true);
  const [selectedPastSymbol, setSelectedPastSymbol] = useState<PastSymbolData>({
    symbol: "",
  });
  const [selectedRetestSymbol, setSelectedRetestSymbol] =
    useState<RetestSymbolData>({ symbol: "" });
  const [lastSelectedType, setLastSelectedType] = useState<
    "past" | "retest" | null
  >(null);
  const [pastDerivative30, setPastderivative30] = useState<boolean>(false);
  const [pastDerivative15, setPastderivative15] = useState<boolean>(false);
  const [orderBlock30Days, setOrderBlock30Days] = useState<
    OrderBlockForPastDays[]
  >([]);
  const [orderBlock15Days, setOrderBlock15Days] = useState<
    OrderBlockForRetest[]
  >([]);

  const lastSelectedTypeRef = useRef(lastSelectedType);
  lastSelectedTypeRef.current = lastSelectedType;

  const updateSymbolFlag = useCallback(
    (
      symbol: string,
      field: "added_to_watchlist" | "added_to_portfolio",
      value: boolean,
    ) => {
      const normalized = normalizeSymbol(symbol);
      const updater = <T extends { symbol: string }>(prev: T[]): T[] =>
        prev.map((item) =>
          normalizeSymbol(item.symbol) === normalized
            ? { ...item, [field]: value }
            : item,
        );
      setOrderBlock30Days(updater);
      setOrderBlock15Days(updater);
    },
    [],
  );

  const handleWatchlistStatusUpdate = useCallback(
    (symbol: string) => updateSymbolFlag(symbol, "added_to_watchlist", true),
    [updateSymbolFlag],
  );

  const handleWatchlistRemove = useCallback(
    (symbol: string) => updateSymbolFlag(symbol, "added_to_watchlist", false),
    [updateSymbolFlag],
  );

  const handlePortfolioStatusUpdate = useCallback(
    (symbol: string) => updateSymbolFlag(symbol, "added_to_portfolio", true),
    [updateSymbolFlag],
  );

  const handleOrderBlockAPIfor30Days = useCallback(async () => {
    setIsPastLoading(true);
    try {
      const response = await fetchWrapper.get(
        `/api/dashboard/order-blocks?derivatives=${!pastDerivative30}`,
        undefined,
        { Authorization: `Bearer ${tokenRef.current}` },
      );
      const data = response.data || [];
      setOrderBlock30Days(data);

      if (data.length > 0) {
        if (
          lastSelectedTypeRef.current === "past" ||
          lastSelectedTypeRef.current === null
        ) {
          const first = data[0];
          setSelectedPastSymbol({
            symbol: normalizeSymbol(first.symbol),
            ob_date: first.ob_date,
          });
          setLastSelectedType("past");
        }
      } else {
        setSelectedPastSymbol({ symbol: "" });
        if (lastSelectedTypeRef.current === "past") {
          setLastSelectedType(null);
        }
      }
    } catch (error) {
      console.error("Request failed:", error);
      alertService.error("Something went wrong. Please try again later.");
    } finally {
      setIsPastLoading(false);
    }
  }, [pastDerivative30]);

  const handleOrderBlockAPIfor15Days = useCallback(async () => {
    setIsRetestLoading(true);
    try {
      const response = await fetchWrapper.get(
        `/api/dashboard/order-blocks/retest?derivatives=${!pastDerivative15}`,
        undefined,
        { Authorization: `Bearer ${tokenRef.current}` },
      );
      const data = response.data || [];
      setOrderBlock15Days(data);

      if (data.length > 0) {
        if (lastSelectedTypeRef.current === "retest") {
          const first = data[0];
          setSelectedRetestSymbol({
            symbol: normalizeSymbol(first.symbol),
            ob_date: first.ob_date,
            retest_date: first.retest_date,
          });
        }
      } else {
        if (lastSelectedTypeRef.current === "retest") {
          setSelectedRetestSymbol({ symbol: "" });
        }
      }
    } catch (error) {
      console.error("Request failed:", error);
      alertService.error("Something went wrong. Please try again later.");
    } finally {
      setIsRetestLoading(false);
    }
  }, [pastDerivative15]);

  useEffect(() => {
    handleOrderBlockAPIfor30Days();
  }, [handleOrderBlockAPIfor30Days]);

  useEffect(() => {
    handleOrderBlockAPIfor15Days();
  }, [handleOrderBlockAPIfor15Days]);

  const handlePastRowSelect = useCallback((row: OrderBlockForPastDays) => {
    setSelectedPastSymbol({
      symbol: normalizeSymbol(row.symbol),
      ob_date: row.ob_date,
    });
    setSelectedRetestSymbol({ symbol: "" });
    setLastSelectedType("past");
  }, []);

  const handleRetestRowSelect = useCallback((row: OrderBlockForRetest) => {
    setSelectedRetestSymbol({
      symbol: normalizeSymbol(row.symbol),
      ob_date: row.ob_date,
      retest_date: row.retest_date,
    });
    setSelectedPastSymbol({ symbol: "" });
    setLastSelectedType("retest");
  }, []);

  const chartData = useMemo(() => {
    if (lastSelectedType === "past") {
      return {
        symbol: selectedPastSymbol.symbol,
        obDate: selectedPastSymbol.ob_date,
        retestDate: undefined,
      };
    }
    if (lastSelectedType === "retest") {
      return {
        symbol: selectedRetestSymbol.symbol,
        obDate: selectedRetestSymbol.ob_date,
        retestDate: selectedRetestSymbol.retest_date,
      };
    }
    return { symbol: "", obDate: undefined, retestDate: undefined };
  }, [lastSelectedType, selectedPastSymbol, selectedRetestSymbol]);

  return (
    <div className="flex flex-col lg:flex-row gap-2 gap-x-2 p-0 mb-3 h-[92vh] min-h-[850px]">
      <div className="w-full lg:w-[60%] flex flex-col gap-2 h-full">
        <div className="bg-white dark:bg-[#121419] rounded-lg shadow-sm flex-1 h-[49%] overflow-hidden">
          <PastBlocksTable
            data={orderBlock30Days}
            className="h-full"
            onWatchlistUpdate={handleWatchlistStatusUpdate}
            onWatchlistRemove={handleWatchlistRemove}
            onPortfolioUpdate={handlePortfolioStatusUpdate}
            onRowSelect={handlePastRowSelect}
            selectedRow={selectedPastSymbol}
            setPastderivative30={setPastderivative30}
            pastDerivative30={pastDerivative30}
            loading={isPastLoading}
          />
        </div>
        <div className="bg-white dark:bg-[#121419] rounded-lg shadow-sm flex-1 h-[49%] overflow-hidden">
          <RetestOrderTable
            data={orderBlock15Days}
            className="h-full"
            onWatchlistUpdate={handleWatchlistStatusUpdate}
            onWatchlistRemove={handleWatchlistRemove}
            onPortfolioUpdate={handlePortfolioStatusUpdate}
            onRowSelect={handleRetestRowSelect}
            setPastderivative15={setPastderivative15}
            pastDerivative15={pastDerivative15}
            selectedRow={selectedRetestSymbol}
            loading={isRetestLoading}
          />
        </div>
      </div>
      <div className="w-full lg:w-[40%] flex flex-col gap-2 mt-2 lg:mt-0 h-full">
        <div className="h-full">
          <OrderBlockChart
            symbol={chartData.symbol}
            obDate={chartData.obDate}
            retestDate={chartData.retestDate}
          />
        </div>
      </div>
    </div>
  );
};

export default OrderBlock;
